import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-02-22'
        tag='Kidizen'
        title="Selling On Kidizen? Here's a Refresher for How Their Shipping Works"
      >
        <p>
          Given the rapid growth of children, parents may find themselves constantly buying clothes, trying to catch up
          to the ever-changing sizes of their kids. Thankfully, there are platforms like Kidizen where parents can buy
          and sell pre-loved garbs to make room for new ones. From endearing garments for infants and adorable apparel
          for toddlers to stylish outfits for girls and boys, Kidizen has these to offer and more.
        </p>
        <p>
          Whether you are a new or a veteran seller, you may want to find tips to better sell on this online
          marketplace. But selling is not limited to marketing. This includes logistics like shipping as well. If you
          are confused about what to do after making a sale or simply want a refresher, this guide will assist you.
        </p>

        <h3>Listing with Shipping</h3>
        <p>
          When you list items on Kidizen, you have the option to include the shipping fee or not. When you decide to
          opt-out, the website automatically shows you an estimated shipping cost.
        </p>
        <p>
          The estimate shows up when you click the button at the bottom of the app while you are on the listing page.
          You are free not to weigh the items beforehand and simply assess.
        </p>
        <p>
          When you have bundles, the discount applies only to the entire bundle price and not the shipping fee. But if
          the bundles' cost already includes shipping, the discount applies to the total price.
        </p>
        <p>
          A pro-tip: you can help your buyer save fees when they have multiple items by making them an offer. The latter
          should already include the shipping cost.
        </p>

        <h3>Shipping Labels</h3>
        <p>
          There are many Kidizen labels you can choose from. Each one has a specific weight, so you should weigh your
          items before packing or making an intelligent estimate. This is to avoid any inconvenience in case your
          package turns out to go beyond the weight you initially indicated.
        </p>
        <p>
          For instance, if you are shipping three (3) dresses and four (4) pairs of pants, either weigh them first to
          get the accurate label or do an estimate (these may be under "Large"). But if you choose "Medium," your
          package will be flagged for being overweight.
        </p>
        <p>Here is the short guide for Kidizen's shipping labels:</p>
        <ul>
          <li>Small (less than 8 oz.) = $4.15</li>
          <li>Medium (less than 12 oz.) = $5.15</li>
          <li>Large (less than 16 oz.) = $6.15</li>
          <li>Extra Large (less than 5 kg) = $9.50</li>
          <li>XX-Large (less than 10 kg) = 15.00</li>
          <li>Jumbo (less than 20 kg) = $22.00</li>
        </ul>
        <p>
          As for the materials, you can use boxes, bags, bubble wrap, etc. Make sure to include this in weighing your
          entire package. However, avoid using any carrier-provided packaging. This may lead to confusion among
          different shipping carriers and may cause you unnecessary hassle.
        </p>
        <p>
          Now, unless you purchase a Kidizen label, the marketplace fee is taken off from your listing. This saves you
          from additional cost and gives you assurance if you purchase the website's label right away.
        </p>
        <p>
          Moreover, the platform provides insurance for the package. If it gets lost during the delivery process or gets
          damaged, Kidizen covers all costs and even sends a refund to the buyer. However, this remains on a
          case-to-case basis.
        </p>
        <p>
          If you purchased a third-party label, you are responsible for contacting the carrier and discussing options in
          resolving the issue.
        </p>

        <h3>Shipping Process</h3>
        <p>
          Once you have closed a deal, the website prompts you to purchase a shipping label. Preferably, choose Kidizen
          for convenience. But if you decide to buy elsewhere (USPS, FedEx, or UPS), make sure to enter a tracking
          number in the "Order Detail" package.{' '}
        </p>
        <p>
          A tracking number is crucial as it serves as your receipt to get paid and as your proof in case the buyer
          inquires about their order and/or something happens to the parcel. If you already shipped the orders, but
          there is no tracking number, submit a request for assistance{' '}
          <a href='https://support.kidizen.com/hc/en-us/requests/new'>here</a>.
        </p>
        <p>When you do opt for a Kidizen label, simply follow these steps:</p>
        <ol>
          <li>Go to your "Order History"</li>
          <li>Select "Purchase Shipping Label"</li>
          <li>
            Choose the best label (remember to weigh the items and the packing materials) that corresponds to the exact
            or approximate weight of the entire order.
          </li>
          <li>Wait for the email of the shipping label and the tracking number.</li>
          <li>Print it out and tape them onto your package. </li>
        </ol>
        <p>
          When you are all set to deliver, drop off the parcel in any USPS blue box, the closest post office to you, or
          schedule a USPS pick-up. The latter is the best option when you don't have much time to drop by at the
          designated stops.
        </p>
        <p>
          If you decide to deliver via the post office, take note the shipping fee won't show up on the website
          automatically. After all, it is unaware of the actual value you paid for it. So, it all boils down to which
          option you prefer to take or which one is more convenient for you.
        </p>

        <h3>Note on Shipping Time</h3>
        <p>
          As for the shipping timeline, Kidizen highly suggests sellers ship the order within 48 hours. Buyers are
          typically excited to receive their package, and so for optimal customer satisfaction, ship the parcel right
          away. But if you don't have much time, do it within five (5) days.
        </p>
        <p>
          If you are unable to do so after seven (7) days, the buyer has the option to request a complete refund. But of
          course, no seller would want this. So, if you need more time to process the packaging and shipping, simply
          message the buyer.
        </p>
        <p>
          You can automatically do this by selecting the "Report Shipping Delay" at the bottom of the "Order Detail"
          page. Feel free to modify the message so there is a personal touch and the buyer would be more understanding
          of your situation.
        </p>

        <h3>Tips on Shipping</h3>
        <p>
          Earning optimal customer satisfaction is among your priority goals as a seller. If your buyers are not happy
          with their order and how you packaged or shipped it, they may leave negative reviews. This won't look good on
          your account and your overall reputation on Kidizen. It will likely decrease the chances of closing deals
          again.
        </p>
        <p>
          This is why you should pay attention to the condition or quality of the items you sell and how you pack them.
          An ultimate tip is to come up with ways to protect the orders. Ziploc bags and sealed plastics work best,
          especially in water-proofing items.
        </p>
        <p>
          For clothes, make sure they are folded nicely and properly such that when the buyer takes it out of the box or
          bag, there are no extreme damages to any part–be it a missing button, loose threads, or hard-to-iron-out
          details.
        </p>
        <p>
          The similar goes with other items like bags, shoes, mini umbrellas, kids' accessories. Pack them up nicely
          that they won't get deformed during shipping. It is best to store them into boxes and cover or fill them with
          multiple layers of tissue or paper to avoid scratches.
        </p>
        <p>
          Keep in mind all these tips. After all, you have no more control over the package once it is in the hands of
          the carrier. Many parcels are shipped daily, where some would likely get thrown around in the truck or be
          carelessly handled. Hence, you should do your best to secure the items rather than suffer the consequences of
          poorly-packaged orders.
        </p>

        <h3>Receiving Payment</h3>
        <p>
          After a sale, the money is put on hold under "Kid Bucks." and will only be released once you ship your
          package. This is for the buyer's protection as well in case you don't deliver their orders according to the
          mandatory timeline.
        </p>
        <p>
          When USPS scans it, your pending earnings will become "Redeemable Kid Bucks." This means you can now use it to
          buy other items on Kidizen or transfer them to a bank account or PayPal. You can do this on your "Profile,"
          then "Settings," and click "Kid Bucks."
        </p>
        <p>
          If you are a new account, you have to wait for 72 hours after its creation before you can withdraw your
          earnings.
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
